import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import moment from 'moment';
import { z } from 'zod';
import { useStore } from '@tanstack/react-form';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import formMessages from '~/components/form-messages';
import Spacings from '~/modules/ui-kit/spacings';
import { useAppForm } from '~/components/Form';

import messages from './messages';

const schema = (intl: IntlShape) =>
  z
    .object({
      startDate: z
        .string({
          error: intl.formatMessage(formMessages.required),
        })
        .min(1, intl.formatMessage(formMessages.required)),
      startTime: z
        .string({
          error: intl.formatMessage(formMessages.required),
        })
        .min(1, intl.formatMessage(formMessages.required)),
      endDate: z
        .string({
          error: intl.formatMessage(formMessages.required),
        })
        .min(1, intl.formatMessage(formMessages.required)),
      endTime: z
        .string({
          error: intl.formatMessage(formMessages.required),
        })
        .min(1, intl.formatMessage(formMessages.required)),
    })
    .superRefine((value, ctx) => {
      if (
        value.startTime &&
        parseInt(value.startTime.split(':')[1], 10) % 30 !== 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(
            messages.dateMustBeAlignedToFullOrHalfHour
          ),
          path: ['startTime'],
        });
      }
      if (
        value.endTime &&
        parseInt(value.endTime.split(':')[1], 10) % 30 !== 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(
            messages.dateMustBeAlignedToFullOrHalfHour
          ),
          path: ['endTime'],
        });
      }
      if (
        value.startDate &&
        value.endTime &&
        value.endDate &&
        value.startTime &&
        moment(`${value.endDate}T${value.endTime}`).isBefore(
          moment(`${value.startDate}T${value.startTime}`)
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(messages.endDateMustBeAfterStartDate),
          path: ['endDate'],
        });
      }
    });

type CreateShiftFormProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  }) => void;
  // the week in which the shift should be created
  date: string;
};

const CreateShiftForm = (props: CreateShiftFormProps) => {
  const intl = useIntl();
  const form = useAppForm({
    defaultValues: {
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
    },
    validators: {
      onChange: schema(intl),
    },
    onSubmit({ value }) {
      props.onSubmit(value);
    },
  });

  const startDate = useStore(form.store, (state) => state.values.startDate);
  const weekStart = moment(props.date).startOf('week');
  const weekEnd = moment(props.date).endOf('week');
  const minEndDate = startDate ? moment(startDate, 'YYYY-MM-DD') : weekStart;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="create-shift-dialog-title"
      aria-describedby="create-shift-dialog-description"
      fullWidth
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <DialogTitle id="create-shift-dialog-title">
          <FormattedMessage {...messages.dialogTitle} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="create-shift-dialog-description"
            component="div"
          >
            <Spacings.Stack scale="m">
              <Spacings.Inline>
                <div>
                  <form.AppField name="startDate">
                    {(field) => (
                      <field.DateField
                        label={<FormattedMessage {...messages.startDate} />}
                        minDate={weekStart}
                        maxDate={weekEnd}
                        format="DD.MM.YYYY"
                      />
                    )}
                  </form.AppField>
                </div>
                <div>
                  <form.AppField name="startTime">
                    {(field) => (
                      <field.TextField
                        label={<FormattedMessage {...messages.startTime} />}
                        type="time"
                        slotProps={{
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                  </form.AppField>
                </div>
              </Spacings.Inline>

              <Spacings.Inline>
                <div>
                  <form.AppField name="endDate">
                    {(field) => (
                      <field.DateField
                        label={<FormattedMessage {...messages.endDate} />}
                        minDate={minEndDate}
                        maxDate={weekEnd}
                        format="DD.MM.YYYY"
                      />
                    )}
                  </form.AppField>
                </div>
                <div>
                  <form.AppField name="endTime">
                    {(field) => (
                      <field.TextField
                        label={<FormattedMessage {...messages.endTime} />}
                        type="time"
                        slotProps={{
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                  </form.AppField>
                </div>
              </Spacings.Inline>
            </Spacings.Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} color="primary">
            <FormattedMessage {...messages.cancelShiftCancellation} />
          </Button>
          <form.AppForm>
            <form.SubmitButton>
              <FormattedMessage {...messages.confirmShiftCreation} />
            </form.SubmitButton>
          </form.AppForm>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateShiftForm;
